import store from '@/state/store'
import { Helper } from '@/mixin/helper'
const hasAccessAll = store.getters['identity/hasAccessAll']
const hasTmsUserGroupAccess = hasAccessAll(['tms-user-group:read']) && Helper.methods.isSuperUser()

import * as Customer from '@/services/rest-api/lib/customer/customer'
import * as MarketingUser from '@/services/rest-api/lib/customer/marketing-user'

import { GET_USER_GROUP } from '@/services/api/setting/user/userGroup'
import { TMS_MARKETING_USER_FILTER } from '@/helpers/tms-access'

const translate = (t, key, fallback, params = {}) => {
  if (typeof t !== 'function') return fallback

  const translated = t(key, params)

  return translated === key ? fallback : translated
}

const selectField = (t, key, fallback) =>
  `${translate(t, 'global.form.select', 'Pilih')} ${translate(t, key, fallback)}`

const enterField = (t, key, fallback) =>
  `${translate(t, 'global.form.enter', 'Masukkan')} ${translate(t, key, fallback)}`

const getCustomerTableFields = (t) => [
  {
    label: translate(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
    name: 'name',
    key: 'name',
    isSortable: true,
    isRedirectable: true,
    isEditable: true,
    validation: 'required',
    placeholder: enterField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
    thStyle: 'width: 350px',
  },
  {
    label: translate(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
    key: 'marketing_user.name',
    isSortable: true,
    isRedirectable: true,
    isEditable: true,
    type: 'select-resourceful',
    name: 'marketing_user_id',
    optionsGetter: MarketingUser.get(TMS_MARKETING_USER_FILTER),
    optionLabelFn: ({ item }) => item.name,
    placeholder: selectField(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
  },
]

const getUserGroupTableFields = (t) => [
  {
    label: translate(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    key: 'name',
    isSortable: false,
  },
  {
    label: translate(t, 'delivery.fields.vehicleGroup', 'Grup Kendaraan'),
    key: 'vgroups',
    isSortable: false,
    formatter: (value) => {
      return value?.length != null
        ? value.length === 0
          ? translate(t, 'delivery.fields.allVehicleGroup', 'Semua Grup Kendaraan')
          : value.length
        : '-'
    },
  },
]

const getDraftFields = (t) => [
  {
    name: 'customer_id',
    showKey: 'shipped_customer',
    type: 'value-help-v2',
    label: translate(t, 'delivery.fields.shippedCustomerName', 'Pelanggan Penerima'),
    placeholder: selectField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
    validation: 'required',
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Customer.get,
    hasStoreAction: true,
    // specific for store action in value help
    apiClient: {
      store: Customer.store,
      show: Customer.show,
    },
    tableFields: getCustomerTableFields(t),
  },
  {
    name: 'billed_customer_id',
    showKey: 'billed_customer',
    type: 'value-help-v2',
    label: translate(t, 'delivery.fields.billedCustomerName', 'Pelanggan Tertagih'),
    placeholder: selectField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
    validation: 'required',
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Customer.get,
    hasStoreAction: true,
    // specific for store action in value help
    apiClient: {
      store: Customer.store,
      show: Customer.show,
    },
    tableFields: getCustomerTableFields(t),
    disabled: true,
  },
  {
    name: 'marketing_user_id',
    showKey: 'marketing_user',
    type: 'select-resourceful-v2',
    label: translate(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
    optionsGetter: MarketingUser.get(TMS_MARKETING_USER_FILTER),
    optionLabelFn: ({ item }) => item.name,
    placeholder: selectField(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
  },
  {
    key: 'aju_number',
    name: 'aju_number',
    label: translate(t, 'delivery.fields.ajuNumber', 'No. AJU'),
    placeholder: enterField(t, 'delivery.fields.ajuNumber', 'No. AJU'),
    type: 'text',
    validation: 'bail|max:100,length',
  },
  // Archieved for Refinement
  // {
  //   key: 'e_seal_number',
  //   name: 'e_seal_number',
  //   label: 'No. E-Seal',
  //   placeholder: 'Masukkan No. E-Seal',
  //   type: 'text',
  //   validation: 'bail|max:100,length',
  // },
  {
    key: 'shipment_number',
    name: 'shipment_number',
    label: translate(t, 'delivery.fields.shipmentNumber', 'No. Shipment'),
    placeholder: enterField(t, 'delivery.fields.shipmentNumber', 'No. Shipment'),
    type: 'text',
    validation: 'bail|max:100,length',
  },
  {
    key: 'reference_number',
    name: 'reference_number',
    label: translate(t, 'delivery.fields.referenceNumberText', 'No. Referensi'),
    placeholder: enterField(t, 'delivery.fields.referenceNumberText', 'No. Referensi'),
    type: 'text',
    validation: 'bail|max:100,length',
  },
  {
    name: 'user_group_id',
    isShow: hasTmsUserGroupAccess,
    showKey: 'user_group',
    label: translate(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    type: 'value-help-v2',
    placeholder: selectField(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: async function (opts) {
      let result = {}
      const filter = {
        pagination: {
          page: opts?.page,
          show: opts?.limit,
        },
        search: opts?.search,
      }

      const res = await this.$apollo.query({
        query: GET_USER_GROUP,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            ...filter,
          },
        },
      })

      const ugroups = res.data.ugroups.ugroups

      result = {
        metadata: {
          total_count: ugroups.pagination.total,
          page: ugroups.pagination.page,
        },
        data: {
          paginated_result: ugroups.data,
        },
      }

      return result
    },
    multiple: false,
    tableFields: getUserGroupTableFields(t),
  },
]

const getConfirmedFields = (t) => [
  {
    name: 'customer_id',
    showKey: 'shipped_customer',
    type: 'value-help-v2',
    label: translate(t, 'delivery.fields.shippedCustomerName', 'Pelanggan Penerima'),
    placeholder: selectField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
    displayedBy: 'name',
    disabled: true,
  },
  {
    name: 'billed_customer_id',
    showKey: 'billed_customer',
    type: 'value-help-v2',
    label: translate(t, 'delivery.fields.billedCustomerName', 'Pelanggan Tertagih'),
    placeholder: selectField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Customer.get,
    hasStoreAction: true,
    // specific for store action in value help
    apiClient: {
      store: Customer.store,
      show: Customer.show,
    },
    tableFields: getCustomerTableFields(t),
    disabled: true,
  },
  {
    name: 'marketing_user_id',
    showKey: 'marketing_user',
    type: 'select-resourceful-v2',
    label: translate(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
    optionLabelFn: ({ item }) => item.name,
    placeholder: translate(
      t,
      'delivery.placeholder.marketingEmployeeUndefined',
      'Karyawan Pemasaran tidak ditentukan'
    ),
    disabled: true,
  },
  {
    key: 'aju_number',
    name: 'aju_number',
    label: translate(t, 'delivery.fields.ajuNumber', 'No. AJU'),
    placeholder: enterField(t, 'delivery.fields.ajuNumber', 'No. AJU'),
    type: 'text',
    validation: 'bail|max:100,length',
  },
  // Archieved for Refinement
  // {
  //   key: 'e_seal_number',
  //   name: 'e_seal_number',
  //   label: 'No. E-Seal',
  //   placeholder: 'Masukkan No. E-Seal',
  //   type: 'text',
  //   validation: 'bail|max:100,length',
  // },
  {
    key: 'shipment_number',
    name: 'shipment_number',
    label: translate(t, 'delivery.fields.shipmentNumber', 'No. Shipment'),
    placeholder: enterField(t, 'delivery.fields.shipmentNumber', 'No. Shipment'),
    type: 'text',
    validation: 'bail|max:100,length',
  },
  {
    key: 'reference_number',
    name: 'reference_number',
    label: translate(t, 'delivery.fields.referenceNumberText', 'No. Referensi'),
    placeholder: enterField(t, 'delivery.fields.referenceNumberText', 'No. Referensi'),
    type: 'text',
    validation: 'bail|max:100,length',
  },
  {
    name: 'user_group_id',
    isShow: hasTmsUserGroupAccess,
    showKey: 'user_group',
    label: translate(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    type: 'value-help-v2',
    placeholder: selectField(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    trackBy: 'id',
    displayedBy: 'name',
    validation: 'required',
    listGetter: async function (opts) {
      let result = {}
      const filter = {
        pagination: {
          page: opts?.page,
          show: opts?.limit,
        },
        search: opts?.search,
      }

      const res = await this.$apollo.query({
        query: GET_USER_GROUP,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            ...filter,
          },
        },
      })

      const ugroups = res.data.ugroups.ugroups

      result = {
        metadata: {
          total_count: ugroups.pagination.total,
          page: ugroups.pagination.page,
        },
        data: {
          paginated_result: ugroups.data,
        },
      }

      return result
    },
    multiple: false,
    tableFields: getUserGroupTableFields(t),
  },
]

const getScheduledFields = (t) => [
  {
    name: 'customer_id',
    showKey: 'shipped_customer',
    type: 'value-help-v2',
    label: translate(t, 'delivery.fields.shippedCustomerName', 'Pelanggan Penerima'),
    placeholder: selectField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
    displayedBy: 'name',
    disabled: true,
  },
  {
    name: 'billed_customer_id',
    showKey: 'billed_customer',
    type: 'value-help-v2',
    label: translate(t, 'delivery.fields.billedCustomerName', 'Pelanggan Tertagih'),
    placeholder: selectField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Customer.get,
    hasStoreAction: true,
    // specific for store action in value help
    apiClient: {
      store: Customer.store,
      show: Customer.show,
    },
    tableFields: getCustomerTableFields(t),
    disabled: true,
  },
  {
    name: 'marketing_user_id',
    showKey: 'marketing_user',
    type: 'select-resourceful-v2',
    label: translate(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
    optionLabelFn: ({ item }) => item.name,
    placeholder: translate(
      t,
      'delivery.placeholder.marketingEmployeeUndefined',
      'Karyawan Pemasaran tidak ditentukan'
    ),
    disabled: true,
  },
  {
    key: 'aju_number',
    name: 'aju_number',
    label: translate(t, 'delivery.fields.ajuNumber', 'No. AJU'),
    placeholder: enterField(t, 'delivery.fields.ajuNumber', 'No. AJU'),
    type: 'text',
    validation: 'bail|max:100,length',
  },
  // Archieved for Refinement
  // {
  //   key: 'e_seal_number',
  //   name: 'e_seal_number',
  //   label: 'No. E-Seal',
  //   placeholder: 'Masukkan No. E-Seal',
  //   type: 'text',
  //   validation: 'bail|max:100,length',
  // },
  {
    key: 'shipment_number',
    name: 'shipment_number',
    label: translate(t, 'delivery.fields.shipmentNumber', 'No. Shipment'),
    placeholder: enterField(t, 'delivery.fields.shipmentNumber', 'No. Shipment'),
    type: 'text',
    validation: 'bail|max:100,length',
  },
  {
    key: 'reference_number',
    name: 'reference_number',
    label: translate(t, 'delivery.fields.referenceNumberText', 'No. Referensi'),
    placeholder: enterField(t, 'delivery.fields.referenceNumberText', 'No. Referensi'),
    type: 'text',
    validation: 'bail|max:100,length',
  },
  {
    name: 'user_group_id',
    isShow: hasTmsUserGroupAccess,
    showKey: 'user_group',
    label: translate(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    type: 'value-help-v2',
    placeholder: selectField(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    trackBy: 'id',
    displayedBy: 'name',
    validation: 'required',
    disabled: true,
    listGetter: async function (opts) {
      let result = {}
      const filter = {
        pagination: {
          page: opts?.page,
          show: opts?.limit,
        },
        search: opts?.search,
      }

      const res = await this.$apollo.query({
        query: GET_USER_GROUP,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            ...filter,
          },
        },
      })

      const ugroups = res.data.ugroups.ugroups

      result = {
        metadata: {
          total_count: ugroups.pagination.total,
          page: ugroups.pagination.page,
        },
        data: {
          paginated_result: ugroups.data,
        },
      }

      return result
    },
    multiple: false,
    tableFields: getUserGroupTableFields(t),
  },
]

const getStartedFields = (t) => [
  {
    name: 'customer_id',
    showKey: 'shipped_customer',
    type: 'value-help-v2',
    label: translate(t, 'delivery.fields.shippedCustomerName', 'Pelanggan Penerima'),
    placeholder: selectField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
    displayedBy: 'name',
    disabled: true,
  },
  {
    name: 'billed_customer_id',
    showKey: 'billed_customer',
    type: 'value-help-v2',
    label: translate(t, 'delivery.fields.billedCustomerName', 'Pelanggan Tertagih'),
    placeholder: selectField(t, 'delivery.fields.customerName', 'Nama Pelanggan'),
    trackBy: 'id',
    displayedBy: 'name',
    listGetter: Customer.get,
    hasStoreAction: true,
    // specific for store action in value help
    apiClient: {
      store: Customer.store,
      show: Customer.show,
    },
    tableFields: getCustomerTableFields(t),
    disabled: true,
  },
  {
    name: 'marketing_user_id',
    showKey: 'marketing_user',
    type: 'select-resourceful-v2',
    label: translate(t, 'delivery.fields.marketingEmployee', 'Karyawan Pemasaran'),
    optionLabelFn: ({ item }) => item.name,
    placeholder: translate(
      t,
      'delivery.placeholder.marketingEmployeeUndefined',
      'Karyawan Pemasaran tidak ditentukan'
    ),
    disabled: true,
  },
  {
    key: 'aju_number',
    name: 'aju_number',
    label: translate(t, 'delivery.fields.ajuNumber', 'No. AJU'),
    placeholder: enterField(t, 'delivery.fields.ajuNumber', 'No. AJU'),
    type: 'text',
    disabled: true,
  },
  // Archieved for Refinement
  // {
  //   key: 'e_seal_number',
  //   name: 'e_seal_number',
  //   label: 'No. E-Seal',
  //   placeholder: 'Masukkan No. E-Seal',
  //   type: 'text',
  //   disabled: true,
  // },
  {
    key: 'shipment_number',
    name: 'shipment_number',
    label: translate(t, 'delivery.fields.shipmentNumber', 'No. Shipment'),
    placeholder: enterField(t, 'delivery.fields.shipmentNumber', 'No. Shipment'),
    type: 'text',
    disabled: true,
  },
  {
    key: 'reference_number',
    name: 'reference_number',
    label: translate(t, 'delivery.fields.referenceNumberText', 'No. Referensi'),
    placeholder: enterField(t, 'delivery.fields.referenceNumberText', 'No. Referensi'),
    type: 'text',
    disabled: true,
  },
  {
    name: 'user_group_id',
    isShow: hasTmsUserGroupAccess,
    showKey: 'user_group',
    label: translate(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    type: 'value-help-v2',
    placeholder: selectField(t, 'delivery.fields.userGroup', 'Grup Pengguna'),
    trackBy: 'id',
    displayedBy: 'name',
    validation: 'required',
    disabled: true,
    listGetter: async function (opts) {
      let result = {}
      const filter = {
        pagination: {
          page: opts?.page,
          show: opts?.limit,
        },
        search: opts?.search,
      }

      const res = await this.$apollo.query({
        query: GET_USER_GROUP,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            ...filter,
          },
        },
      })

      const ugroups = res.data.ugroups.ugroups

      result = {
        metadata: {
          total_count: ugroups.pagination.total,
          page: ugroups.pagination.page,
        },
        data: {
          paginated_result: ugroups.data,
        },
      }

      return result
    },
    multiple: false,
    tableFields: getUserGroupTableFields(t),
  },
]

const getDeliveryDetailFields = (t, _locale) => ({
  Draf: getDraftFields(t),
  Dikonfirmasi: getConfirmedFields(t),
  'Menunggu Vendor': getConfirmedFields(t),
  Ditolak: getConfirmedFields(t),
  'Diterima Vendor': getConfirmedFields(t),
  Dijadwalkan: getScheduledFields(t),
  'Dijadwalkan Ulang': getConfirmedFields(t),
  Dijalankan: getStartedFields(t),
  Diambil: getStartedFields(t),
  Terkirim: getStartedFields(t),
  Dibatalkan: getStartedFields(t),
  'Sedang Transit': getStartedFields(t),

  default: getDraftFields(t),
})

export default getDeliveryDetailFields
